import React from "react";

import { Link, Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import { BarChart3, Briefcase, Building2, LayoutDashboard, Package, Truck } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ReleaseNotice } from "@/components/informative/release-notice";
import { AppSidebar } from "@/components/navigation/app-sidebar";
import { CommandMenu } from "@/components/navigation/command-menu";
import { ChildrenType, NavGroup, NavMainItem } from "@/components/navigation/nav-main";
import { OnboardingProvider } from "@/components/onboarding/onboarding-provider";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { BreadcrumbProvider } from "@/contexts/breadcrumb-context";
import { USER_ROLES } from "@/core/auth/permissions-registry";
import useCrumbs from "@/hooks/use-crumbs";

export const Route = createFileRoute("/_secure")({
  component: SecureLayout,
  beforeLoad: ({ context, location }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({
        to: "/login",
        search: {
          redirect: location.href,
        },
      });
    }
  },
});

function SecureLayout() {
  return (
    <OnboardingProvider>
      <BreadcrumbProvider>
        <SecureLayoutContent />
      </BreadcrumbProvider>
    </OnboardingProvider>
  );
}

function SecureLayoutContent() {
  const { t } = useTranslation();
  const crumbs = useCrumbs();

  const navGroups: NavGroup[] = [
    {
      items: [
        {
          title: t("navigation.dashboard"),
          url: "/",
          icon: LayoutDashboard,
          onboardingId: "dashboard",
        },
        {
          title: t("navigation.deliveries"),
          url: "/deliveries",
          icon: Truck,
          onboardingId: "deliveries",
        },
        {
          title: t("navigation.accounts"),
          url: "/accounts",
          icon: Briefcase,
          onboardingId: "accounts",
        },
        {
          title: t("navigation.reporting"),
          url: "/reporting",
          icon: BarChart3,
          onboardingId: "reporting",
        },
      ],
    },
    {
      label: t("navigation.administration"),
      childrenType: ChildrenType.GROUP,
      items: [
        {
          title: t("navigation.companies"),
          url: "/companies",
          icon: Building2,
          requiredRoles: USER_ROLES.ROLE_SUPER_ADMIN,
        },
        {
          title: t("navigation.onboarding"),
          url: "/onboarding",
          icon: Package,
          requiredRoles: USER_ROLES.ROLE_SUPER_ADMIN,
        },
      ],
    },
  ];

  // Flatten items for CommandMenu
  const allNavItems: NavMainItem[] = navGroups.flatMap((group) => group.items);

  return (
    <SidebarProvider>
      <CommandMenu items={allNavItems} />
      <AppSidebar groups={navGroups} />
      <SidebarInset>
        <header className="flex h-12 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList data-testid="breadcrumb">
                <BreadcrumbItem className="hidden md:block">
                  <BreadcrumbLink asChild>
                    <Link to="/">{t("navigation.dashboard")}</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {crumbs.map((crumb) => (
                  <React.Fragment key={crumb.pathname}>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem className="hidden md:block">
                      {crumb.isLast || !crumb.isNavigable ? (
                        <BreadcrumbPage className="text-muted-foreground">{crumb.label}</BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink asChild>
                          <Link to={crumb.pathname}>{crumb.label}</Link>
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>
        <div className="px-4 py-2">
          <ReleaseNotice className="mb-2" />
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
